/**
 * Product enquiry drawer.
 * The form submits natively (Shopify contact form -> page reload); this
 * component adds the UX and tracking around it:
 * - keeps the variant URL in the prefilled message current when the drawer opens
 * - fires GA events (product_enquiry_opened / product_enquiry_submitted) via
 *   window.themeAnalytics and Klaviyo identify + "Product Enquire" on submit
 * - re-opens the drawer after the post-submit reload so the success message
 *   inside it is visible (localStorage flag survives the reload)
 */
if (!customElements.get('product-enquiry-form')) {
  customElements.define(
    'product-enquiry-form',
    class ProductEnquiryForm extends HTMLElement {
      connectedCallback() {
        this.modal = this.closest('modal-component');
        this.form = this.querySelector('form');
        if (!this.modal || !this.form) return;

        this.suppressOpenTracking = false;

        this.observer = new MutationObserver(() => {
          if (!this.modal.classList.contains('show')) return;
          this.refreshVariantUrl();
          if (this.suppressOpenTracking) {
            this.suppressOpenTracking = false;
            return;
          }
          this.track('product_enquiry_opened');
        });
        this.observer.observe(this.modal, { attributes: true, attributeFilter: ['class'] });

        this.form.addEventListener('submit', () => {
          this.setSubmitting(true);
          localStorage.setItem('product-enquiry-submitted', 'true');
          this.track('product_enquiry_submitted');
          this.klaviyoTrack();
          /* Native POST normally navigates away; if it doesn't (e.g. Shopify's
             hCaptcha intercepts the submission), release the button so the
             form stays usable */
          this.submitResetTimer = setTimeout(() => this.setSubmitting(false), 10000);
        });

        if (localStorage.getItem('product-enquiry-submitted') === 'true') {
          localStorage.removeItem('product-enquiry-submitted');
          this.suppressOpenTracking = true;
          this.openModal();
        }

        /* Back/forward-cache restore (e.g. Back after submitting) revives the
           page with the button still in its loading state - release it */
        this.onPageShow = (event) => {
          if (event.persisted) this.setSubmitting(false);
        };
        window.addEventListener('pageshow', this.onPageShow);
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.submitResetTimer) clearTimeout(this.submitResetTimer);
        if (this.onPageShow) window.removeEventListener('pageshow', this.onPageShow);
      }

      /* Dawn button loading convention: .loading class shows the spinner.
         Disabled is set after the submit event has fired, so the native
         submission itself is unaffected. */
      setSubmitting(submitting) {
        const button = this.form.querySelector('button[type="submit"]');
        if (!button) return;
        button.classList.toggle('loading', submitting);
        if (submitting) {
          button.setAttribute('disabled', 'disabled');
        } else {
          button.removeAttribute('disabled');
        }
      }

      /* Same steps as ModalComponent.showModal, without needing an opener click */
      openModal() {
        const opener = document.querySelector('[data-modal-target="' + this.modal.id + '"]');
        if (opener) {
          opener.classList.add('active');
          opener.setAttribute('aria-expanded', true);
        }
        document.documentElement.classList.add('open-modal');
        this.modal.classList.add('show');
        setTimeout(() => this.modal.focus(), 300);
      }

      getProductUrl() {
        const base = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
        const variantId = this.currentVariantId();
        return variantId ? `${base}?variant=${variantId}` : base;
      }

      currentVariantId() {
        const input = document.querySelector('input.product-variant-id, input[name="id"]');
        if (input && input.value) return input.value;
        return new URLSearchParams(window.location.search).get('variant');
      }

      refreshVariantUrl() {
        const textarea = this.querySelector('textarea[name="contact[message]"]');
        const variantId = this.currentVariantId();
        if (!textarea || !variantId) return;
        textarea.value = textarea.value.replace(/(\?variant=)(\d+)/, '$1' + variantId);
      }

      track(eventName) {
        if (window.themeAnalytics) {
          window.themeAnalytics.trackEvent(eventName, {
            product_url: this.getProductUrl(),
            form_type: 'product_enquiry',
          });
        }
      }

      getFormData() {
        const value = (selector) => {
          const field = this.form.querySelector(selector);
          return field ? field.value.trim() : '';
        };
        return {
          name: value('input[name="contact[name]"]'),
          email: value('input[name="contact[email]"]'),
          phone: value('input[name="contact[phone]"]'),
          message: value('textarea[name="contact[message]"]'),
        };
      }

      klaviyoTrack() {
        try {
          if (typeof window.klaviyo === 'undefined') {
            console.warn('Klaviyo is not loaded. Product enquiry tracking will not work.');
            return;
          }
          const formData = this.getFormData();
          const trackData = {
            event: 'Submitted product enquire',
            Product: this.getProductUrl(),
            timestamp: new Date().toISOString(),
            page_url: window.location.href,
          };
          if (formData.email) trackData.email = formData.email;
          if (formData.name) trackData.name = formData.name;
          if (formData.phone) trackData.phone = formData.phone;
          if (formData.message) trackData.message = formData.message;

          const performTracking = () => klaviyo.track('Product Enquire', trackData);

          if (formData.email) {
            const identifyData = { $email: formData.email };
            if (formData.name) {
              const nameParts = formData.name.split(/\s+/);
              identifyData.$first_name = nameParts[0];
              if (nameParts.length > 1) identifyData.$last_name = nameParts.slice(1).join(' ');
            }
            if (formData.phone) identifyData.$phone_number = formData.phone;
            klaviyo.identify(identifyData, performTracking);
          } else {
            performTracking();
          }
        } catch (error) {
          console.error('Error tracking product enquiry:', error);
        }
      }
    }
  );
}
